<template>
  <div>
    <PopulationFilterOperatorRow
      v-if="showOperator"
      label="Partner Populations"
      :operator="currentOperator"
      @select="handleOperatorSelect"
    />
    <BittsInput
      v-if="showSearch"
      v-model="searchTerm"
      name="searchTerm"
      placeholder="Search populations"
      size="small"
      type="search"
      class="p-6"
    />
    <BittsDivider class="m-0 p-0" />
    <template v-if="filteredTreeData.length">
      <div class="flex items-center gap-8 px-8 pt-8 min-h-[24px]">
        <BittsCheckbox
          aria-label="All partners"
          :checked="isAllPartnersChecked"
          :indeterminate="isAllPartnersIndeterminate"
          @input="onAllPartnersChange"
          @click.stop
        />
        <p class="text-neutral-text-strong text-base">All Partners</p>
      </div>
      <div class="max-h-[300px] max-w-[600px] overflow-y-scroll px-8 pb-8">
        <BittsBaseTree
          :node-data="filteredTreeData"
          :expand-all="isSearchActive"
          :get-node-indeterminate="getSegmentNodeIndeterminate"
          @update="onTreeUpdate"
        >
          <template
            #node-checkbox="{ node, checked, indeterminate, onInput, disabled }"
          >
            <BittsTooltip
              v-if="
                isSavedFilter && (isOrgNode(node.id) || isSegmentNode(node.id))
              "
              placement="topLeft"
              overlay-class="max-w-[200px]"
              @click.stop
            >
              <BittsCheckbox
                :aria-label="node.label"
                :checked="checked"
                :indeterminate="indeterminate"
                :disabled="disabled"
                @input="onInput"
              />
              <template #title
                >If checked, new populations will be included
                automatically</template
              >
            </BittsTooltip>
            <BittsCheckbox
              v-else
              :aria-label="node.label"
              :checked="checked"
              :indeterminate="indeterminate"
              :disabled="disabled"
              @input="onInput"
              @click.stop
            />
          </template>
          <template #node-prefix="{ node }">
            <BittsAvatar
              v-if="isOrgNode(node.id)"
              :org="
                partnersStore.getPartnerOrgById(
                  parseOrgIdFromNodeId(node.id as string),
                )
              "
              :show-initials="true"
              size="x-small"
            />
          </template>
          <template #node-suffix="{ node }">
            <BittsCount
              v-if="node.children"
              :count="getSelectedCount(node)"
              :show-zero="true"
              type="neutral"
            />
          </template>
        </BittsBaseTree>
      </div>
    </template>
    <div v-else class="py-8 text-center">
      <BittsEmptyState
        :font-awesome-icon="['fak', 'search']"
        :font-awesome-icon-style="{ height: '26px', width: '26px' }"
        font-awesome-icon-class="partner-population-filter-tree__empty-state-icon"
        title="No results"
        description="No populations found"
        class="w-[280px] border-none mx-auto"
      />
    </div>
    <div
      v-if="showConvertToAdvanced"
      class="border-t border-neutral-border p-6"
    >
      <BittsOptionListItem
        :option="{
          label: 'Convert to advanced filter',
          value: 'convert_to_advanced_filter',
        }"
        :fa-icon="['fak', 'cycle']"
        @click="emit('convert-to-advanced')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsBaseTree,
  BittsCheckbox,
  BittsCount,
  BittsDivider,
  BittsEmptyState,
  BittsInput,
  BittsOptionListItem,
  BittsTooltip,
} from '@crossbeam/bitts';
import { TreeNodeType } from '@crossbeam/bitts/src/BittsTree/BittsBaseTree.vue';

import { cloneDeep } from 'lodash';
import { computed, ref } from 'vue';

import { SEGMENT_TYPE_MAP } from '@/constants/reports';
import { usePartnersStore, usePopulationsStore } from '@/stores';
import { SegmentType } from '@/types/populations';
import {
  ReportPartnerSegmentFilters,
  ReportPartnerSegmentFiltersItem,
} from '@/types/reports';

import PopulationFilterOperatorRow from './PopulationFilterOperatorRow.vue';

type PopulationMeta = { id: number; name: string; organizationId: number };

type Node = TreeNodeType<PopulationMeta>;

const props = withDefaults(
  defineProps<{
    selectedFilters?: ReportPartnerSegmentFilters;
    showSearch?: boolean;
    showConvertToAdvanced?: boolean;
    showOperator?: boolean;
    /**
     * A saved report keeps a ticked segment or partner as "everything in it",
     * new populations included. Turning this off drops the tooltip promising
     * that, and the empty segments and partners that only that promise makes
     * worth ticking.
     */
    isSavedFilter?: boolean;
    partnerOrgIds?: number[];
  }>(),
  {
    selectedFilters: () => ({ items: [], operator: 'OR' }),
    showSearch: true,
    showConvertToAdvanced: false,
    showOperator: false,
    isSavedFilter: true,
    partnerOrgIds: undefined,
  },
);

const emit = defineEmits<{
  update: [value: ReportPartnerSegmentFilters];
  'convert-to-advanced': [];
}>();

const SEGMENT_LABELS: Record<SegmentType, string> = {
  customers: 'Customers',
  prospects: 'Prospects',
  open_opportunities: 'Open Opportunities',
  other: 'Other',
};

const popsStore = usePopulationsStore();
const partnersStore = usePartnersStore();

const searchTerm = ref('');
const isSearchActive = computed(() => searchTerm.value.trim().length > 0);

const currentOperator = computed<'OR' | 'AND'>(
  () => props.selectedFilters?.operator ?? 'OR',
);

function handleOperatorSelect(op: 'OR' | 'AND') {
  emit('update', {
    operator: op,
    items: props.selectedFilters?.items ?? [],
  });
}

/** Org node IDs follow the pattern `${segment}__${orgId}`. */
function parseOrgIdFromNodeId(nodeId: string): number {
  return parseInt(nodeId.split('__')[1] ?? '0', 10);
}

function isOrgNode(nodeId: string | number): boolean {
  return typeof nodeId === 'string' && nodeId.includes('__');
}

function isSegmentNode(nodeId: string | number): boolean {
  return typeof nodeId === 'string' && !nodeId.includes('__');
}

/**
 * Find the filter item for a given (segment, org) pair.
 * Empty population_ids means "entire segment selected for this org".
 */
function findFilterItem(segment: SegmentType, orgId: number) {
  return props.selectedFilters?.items?.find(
    (f: ReportPartnerSegmentFilters['items'][number]) =>
      f.segment === segment &&
      'organization_id' in f &&
      f.organization_id === orgId,
  );
}

/** Build a lookup of checked population IDs from the selected filters. */
function getCheckedPopIds(): Set<number> {
  const ids = new Set<number>();
  for (const item of props.selectedFilters?.items ?? []) {
    if ('organization_id' in item) {
      if (!item.population_ids?.length) {
        const pops = popsStore
          .getPartnerPopulationsBySegment(item.segment as SegmentType)
          .filter((p) => p.organization_id === item.organization_id);
        for (const pop of pops) ids.add(pop.id);
      } else {
        for (const popId of item.population_ids) ids.add(popId);
      }
    } else {
      const pops = popsStore.getPartnerPopulationsBySegment(
        item.segment as SegmentType,
      );
      for (const pop of pops) ids.add(pop.id);
    }
  }
  return ids;
}

const listedPartnerIds = computed(() =>
  props.partnerOrgIds ? new Set(props.partnerOrgIds) : null,
);

function isListedPartner(orgId: number) {
  return !listedPartnerIds.value || listedPartnerIds.value.has(orgId);
}

const treeData = computed<Node[]>(() => {
  const segmentTypes = Object.values(SEGMENT_TYPE_MAP) as SegmentType[];
  const checkedPopIds = getCheckedPopIds();

  return segmentTypes
    .map((segmentType) => {
      const partnerPops = popsStore
        .getPartnerPopulationsBySegment(segmentType)
        .filter((pop) => isListedPartner(pop.organization_id));

      const byOrg = new Map<number, typeof partnerPops>();
      for (const pop of partnerPops) {
        const list = byOrg.get(pop.organization_id) ?? [];
        list.push(pop);
        byOrg.set(pop.organization_id, list);
      }

      if (props.isSavedFilter) {
        for (const partnerOrg of partnersStore.partnerOrgs) {
          if (isListedPartner(partnerOrg.id) && !byOrg.has(partnerOrg.id)) {
            byOrg.set(partnerOrg.id, []);
          }
        }
      }

      const isSegmentWildcard = (props.selectedFilters?.items ?? []).some(
        (f) =>
          f.segment === segmentType &&
          (!('organization_id' in f) || !f.organization_id),
      );

      const orgNodes: Node[] = [];
      for (const [orgId, pops] of byOrg) {
        const partnerOrg = partnersStore.getPartnerOrgById(orgId);
        const orgName = partnerOrg?.name ?? `Partner ${orgId}`;

        const popNodes: Node[] = pops.map((pop) => ({
          id: pop.id,
          label: pop.name,
          checked: checkedPopIds.has(pop.id),
          metaData: {
            id: pop.id,
            name: pop.name,
            organizationId: pop.organization_id,
          },
        }));

        const filterItem = findFilterItem(segmentType, orgId);
        const isOrgWildcard =
          isSegmentWildcard ||
          (!!filterItem &&
            (!('population_ids' in filterItem) ||
              !filterItem.population_ids?.length));

        orgNodes.push({
          id: `${segmentType}__${orgId}`,
          label: orgName,
          checked: isOrgWildcard,
          indeterminate: !isOrgWildcard && popNodes.some((p) => p.checked),
          children: popNodes.length ? popNodes : undefined,
        });
      }

      return {
        id: segmentType,
        label: SEGMENT_LABELS[segmentType],
        checked: isSegmentWildcard,
        indeterminate:
          !isSegmentWildcard &&
          orgNodes.some((o) => o.checked || o.indeterminate),
        children: orgNodes,
      } as Node;
    })
    .filter((node): node is Node => !!node.children?.length);
});

const filteredTreeData = computed<Node[]>(() => {
  const term = searchTerm.value.trim().toLowerCase();
  if (!term) return treeData.value;

  const result: Node[] = [];
  for (const segmentNode of treeData.value) {
    const filteredOrgs: Node[] = [];
    for (const orgNode of segmentNode.children ?? []) {
      const orgMatches = orgNode.label.toLowerCase().includes(term);
      if (orgMatches) {
        filteredOrgs.push(orgNode);
        continue;
      }

      const filteredPops = (orgNode.children ?? []).filter((popNode) =>
        popNode.label.toLowerCase().includes(term),
      );
      if (filteredPops.length) {
        filteredOrgs.push({ ...orgNode, children: filteredPops } as Node);
      }
    }

    if (filteredOrgs.length) {
      result.push({ ...segmentNode, children: filteredOrgs } as Node);
    }
  }
  return result;
});

function expandSegmentWildcard(
  items: ReportPartnerSegmentFilters['items'],
  segmentType: SegmentType,
) {
  const idx = items.findIndex(
    (f) =>
      f.segment === segmentType &&
      (!('organization_id' in f) || !f.organization_id),
  );
  if (idx > -1) items.splice(idx, 1);

  for (const orgNode of treeData.value.find((s) => s.id === segmentType)
    ?.children ?? []) {
    const orgId = parseOrgIdFromNodeId(orgNode.id as string);
    if (
      !items.some(
        (f) =>
          f.segment === segmentType &&
          'organization_id' in f &&
          f.organization_id === orgId,
      )
    ) {
      items.push({ segment: segmentType, organization_id: orgId });
    }
  }
}

function updateOrgFilterItem(
  items: ReportPartnerSegmentFilters['items'],
  orgNode: Node,
  segmentType: SegmentType,
  isExplicitOrgSelection: boolean,
) {
  const hasSegmentWildcard = items.some(
    (f) =>
      f.segment === segmentType &&
      (!('organization_id' in f) || !f.organization_id),
  );
  if (hasSegmentWildcard) expandSegmentWildcard(items, segmentType);

  const orgId = parseOrgIdFromNodeId(orgNode.id as string);
  const existingIdx = items.findIndex(
    (item) =>
      item.segment === segmentType &&
      'organization_id' in item &&
      item.organization_id === orgId,
  );

  const allOrgPops =
    treeData.value
      .find((s) => s.id === segmentType)
      ?.children?.find((o) => o.id === orgNode.id)?.children ?? [];

  const existingFilter = findFilterItem(segmentType, orgId);
  const isOrgWildcard =
    hasSegmentWildcard ||
    (!!existingFilter &&
      (!('population_ids' in existingFilter) ||
        !existingFilter.population_ids?.length));
  const mergedSelectedIds = new Set<number>(
    isOrgWildcard
      ? allOrgPops.map((p) => p.id as number)
      : (existingFilter?.population_ids ?? []),
  );

  for (const pop of orgNode.children ?? []) {
    if (pop.checked) mergedSelectedIds.add(pop.id as number);
    else mergedSelectedIds.delete(pop.id as number);
  }

  const selectedPopIds = [...mergedSelectedIds];

  if (!selectedPopIds.length) {
    if (isExplicitOrgSelection && orgNode.checked) {
      // Empty org explicitly checked → add org-level wildcard
      const item: ReportPartnerSegmentFiltersItem = {
        segment: segmentType,
        organization_id: orgId,
      };
      if (existingIdx > -1) items[existingIdx] = item;
      else items.push(item);
    } else if (existingIdx > -1) {
      items.splice(existingIdx, 1);
    }
    return;
  }

  const totalPopsInOrg = allOrgPops.length;
  const allSelected = selectedPopIds.length === totalPopsInOrg;

  // Wildcard only when the org row itself (or its parent segment) was explicitly clicked.
  // Individual leaf selections always produce specific IDs, even if all pops happen to be checked.
  const item: ReportPartnerSegmentFiltersItem =
    allSelected && isExplicitOrgSelection
      ? { segment: segmentType, organization_id: orgId }
      : {
          segment: segmentType,
          organization_id: orgId,
          population_ids: selectedPopIds,
        };

  if (existingIdx > -1) items[existingIdx] = item;
  else items.push(item);
}

function getSelectedCount(node: Node): number {
  if (isOrgNode(node.id)) {
    return (node.children ?? []).filter((c) => c.checked).length;
  }
  return (node.children ?? []).reduce(
    (sum, orgNode) =>
      sum + (orgNode.children ?? []).filter((c) => c.checked).length,
    0,
  );
}

function getSegmentNodeIndeterminate(node: Node): boolean {
  if (isOrgNode(node.id)) return node.indeterminate ?? false;
  const segmentType = node.id as SegmentType;
  const items = props.selectedFilters?.items ?? [];
  const isSegmentWildcard = items.some(
    (f) =>
      f.segment === segmentType &&
      (!('organization_id' in f) || !f.organization_id),
  );
  if (isSegmentWildcard) return false;
  return items.some((f) => f.segment === segmentType);
}

const activeSegmentTypes = computed(() =>
  treeData.value.map((n) => n.id as SegmentType),
);

const isAllPartnersChecked = computed(() => {
  const types = activeSegmentTypes.value;
  if (!types.length) return false;
  return types.every((segmentType) =>
    (props.selectedFilters?.items ?? []).some(
      (f) =>
        f.segment === segmentType &&
        (!('organization_id' in f) || !f.organization_id),
    ),
  );
});

const isAllPartnersIndeterminate = computed(() => {
  if (isAllPartnersChecked.value) return false;
  return (props.selectedFilters?.items ?? []).length > 0;
});

function onAllPartnersChange(checked: boolean) {
  if (!checked) {
    emit('update', { operator: currentOperator.value, items: [] });
    return;
  }
  const items = activeSegmentTypes.value.map((segmentType) => ({
    segment: segmentType,
  }));
  emit('update', { operator: currentOperator.value, items });
}

function onTreeUpdate(updatedNodes: Node[], updatedNodeId: number | string) {
  let nextItems = cloneDeep(props.selectedFilters?.items ?? []);

  if (typeof updatedNodeId === 'number') {
    // Leaf population clicked: find and update only the single affected org.
    // All other orgs keep their existing filter entries untouched.
    for (const segmentNode of updatedNodes) {
      for (const orgNode of segmentNode.children ?? []) {
        if (!orgNode.children?.some((p) => p.id === updatedNodeId)) continue;
        updateOrgFilterItem(
          nextItems,
          orgNode,
          segmentNode.id as SegmentType,
          false,
        );
        emit('update', { operator: currentOperator.value, items: nextItems });
        return;
      }
    }
  } else if ((updatedNodeId as string).includes('__')) {
    // Org node explicitly clicked
    const orgNode = updatedNodes
      .flatMap((s) => s.children ?? [])
      .find((o) => o.id === updatedNodeId);
    if (!orgNode) return;
    const segmentType = (updatedNodeId as string).split('__')[0] as SegmentType;
    updateOrgFilterItem(nextItems, orgNode, segmentType, true);
    emit('update', { operator: currentOperator.value, items: nextItems });
  } else {
    const segmentType = updatedNodeId as SegmentType;
    const segmentNode = updatedNodes.find((s) => s.id === segmentType);
    if (!segmentNode) return;

    nextItems = nextItems.reduce<ReportPartnerSegmentFilters['items']>(
      (acc, item) => {
        if (item.segment !== segmentType) acc.push(item);
        return acc;
      },
      [],
    );

    if (segmentNode.checked) {
      nextItems.push({ segment: segmentType });
    }

    emit('update', { operator: currentOperator.value, items: nextItems });
  }
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";

:deep(.ant-scroll-number-only) {
  animation: none;
  transition: none;
}

:deep(.partner-population-filter-tree__empty-state-icon) {
  @apply text-neutral-text-weak p-20 rounded-[36px];
  background: radial-gradient(
      136.96% 69.92% at 50.09% -12.42%,
      theme(colors.neutral.accent / 0.2) 0%,
      theme(colors.neutral.accent / 0) 100%
    ),
    linear-gradient(0deg, theme(colors.white), theme(colors.white));
}
</style>
